import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import type L from 'leaflet';
import { useTheme } from '../../theme';
import {
  getLeaflet,
  makeAccuracyCircle,
  makeUserLocationIcon,
  showLocationError,
} from './leafletHelpers';

interface UseUserLocationArgs {
  mapRef: RefObject<L.Map | null>;
  isLoaded: boolean;
  showUserLocation: boolean;
}

const LOCATE_ZOOM = 15;

// Same options getAccuratePosition() uses on web. enableHighAccuracy turns the
// GPS chip on (without it the browser answers from wifi/cell, tens to hundreds
// of metres out) and maximumAge: 0 refuses a cached fix, so the dot can never
// show where the user was several minutes ago.
const WATCH_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 30000,
  maximumAge: 0,
};

/**
 * Manages the user's current-location marker + the "locate me" button
 * handler on the web map. Safe to pass `showUserLocation=false` — the hook
 * no-ops and `handleLocateMe` is omitted so the caller can skip rendering
 * the button.
 *
 * The dot is driven by a live `watchPosition`, not a one-shot fix: a GPS
 * chip's first reading is the coarse network one and tightens over the next
 * few seconds, so a single reading placed the dot well away from the pin the
 * composer captured through `getAccuratePosition()`. The watch also lets the
 * dot follow someone walking a property.
 *
 * A watch keeps GPS warm and lights the browser's location indicator, so it
 * only runs while the map is actually on screen: `useFocusEffect` covers
 * in-app navigation, the `visibilitychange` listener covers tab switches and
 * backgrounding (which router focus never sees).
 */
export function useUserLocation({ mapRef, isLoaded, showUserLocation }: UseUserLocationArgs): {
  handleLocateMe: () => void;
} {
  const { colors } = useTheme();
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const watchIdRef = useRef<number | null>(null);
  // Newest fix, so the locate-me button can recentre instantly instead of
  // paying for another acquisition.
  const lastFixRef = useRef<{ latitude: number; longitude: number } | null>(null);
  // A denial seen by the self-starting watch, held back until the user asks
  // for their location outright. See the watch's error handler.
  const deniedRef = useRef<GeolocationPositionError | null>(null);
  const isMountedRef = useRef(true);
  const colorRef = useRef(colors.info);

  // Keep the live layers in step with a light/dark toggle. The ref feeds
  // creation; this effect repaints anything already on the map.
  useEffect(() => {
    colorRef.current = colors.info;
    markerRef.current?.setIcon(makeUserLocationIcon(colors.info));
    circleRef.current?.setStyle({ color: colors.info, fillColor: colors.info });
  }, [colors.info]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const renderFix = useCallback(
    (latitude: number, longitude: number, accuracyM: number | null) => {
      const map = mapRef.current;
      if (!isMountedRef.current || !map) return;

      lastFixRef.current = { latitude, longitude };
      const latlng: [number, number] = [latitude, longitude];

      if (markerRef.current) {
        markerRef.current.setLatLng(latlng);
      } else {
        markerRef.current = getLeaflet()
          .marker(latlng, { icon: makeUserLocationIcon(colorRef.current) })
          .addTo(map);
      }

      // A fix with no accuracy number gets no ring — an arbitrary radius would
      // claim a precision we were not told.
      if (accuracyM == null) {
        circleRef.current?.remove();
        circleRef.current = null;
        return;
      }
      if (circleRef.current) {
        circleRef.current.setLatLng(latlng);
        circleRef.current.setRadius(accuracyM);
      } else {
        circleRef.current = makeAccuracyCircle(latlng, accuracyM, colorRef.current).addTo(map);
      }
    },
    [mapRef]
  );

  const stopWatch = useCallback(() => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const startWatch = useCallback(() => {
    if (watchIdRef.current != null) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    // Already refused. Re-asking on every tab switch just burns a failed
    // request; the locate-me button clears this and retries in case they
    // have since granted it.
    if (deniedRef.current) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        renderFix(
          position.coords.latitude,
          position.coords.longitude,
          position.coords.accuracy ?? null
        );
      },
      (err) => {
        // Browsers emit transient POSITION_UNAVAILABLE/TIMEOUT mid-watch and
        // recover on the next reading; only a permission denial is final.
        //
        // Nothing in here may alert. This watch starts by itself when the map
        // loads, and showLocationError() is a window.alert — someone who has
        // denied location would get a dialog thrown at them for merely
        // opening the app, having asked for nothing. (It also wedges headless
        // Chrome: an unhandled alert blocks the renderer, which is how this
        // was caught.) Record the denial and report it only if they press
        // locate-me.
        if (err.code === err.PERMISSION_DENIED) {
          stopWatch();
          deniedRef.current = err;
        }
      },
      WATCH_OPTIONS
    );
  }, [renderFix, stopWatch]);

  // Start/stop with router focus. Runs only once the map exists and the
  // caller actually wants the dot.
  useFocusEffect(
    useCallback(() => {
      if (isLoaded && showUserLocation) startWatch();
      return () => {
        stopWatch();
      };
    }, [isLoaded, showUserLocation, startWatch, stopWatch])
  );

  // Router focus does not fire when the browser tab is switched away or the
  // phone is locked, so the watch would keep GPS hot in the background.
  useEffect(() => {
    if (!isLoaded || !showUserLocation) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') startWatch();
      else stopWatch();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isLoaded, showUserLocation, startWatch, stopWatch]);

  // Drop the watch and both layers on unmount, whichever path got us there.
  useEffect(() => {
    return () => {
      stopWatch();
      markerRef.current?.remove();
      markerRef.current = null;
      circleRef.current?.remove();
      circleRef.current = null;
    };
  }, [stopWatch]);

  const handleLocateMe = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      window.alert('Geolocation is not available in this browser.');
      return;
    }

    // The watch normally has a fix already, so the button is instant.
    const fix = lastFixRef.current;
    if (fix) {
      mapRef.current?.setView([fix.latitude, fix.longitude], LOCATE_ZOOM);
      startWatch();
      return;
    }

    // No fix yet: the first reading is still in flight, or the watch was
    // refused. Either way the press is a fresh request — clear any held
    // denial so the retry below really runs and the watch can restart if
    // permission has since been granted.
    deniedRef.current = null;
    startWatch();
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!isMountedRef.current) return;
        const { latitude, longitude, accuracy } = position.coords;
        renderFix(latitude, longitude, accuracy ?? null);
        mapRef.current?.setView([latitude, longitude], LOCATE_ZOOM);
      },
      (err) => {
        // Alerting is fine here: this call only happens on a button press.
        if (err.code === err.PERMISSION_DENIED) deniedRef.current = err;
        if (isMountedRef.current) showLocationError(err);
      },
      WATCH_OPTIONS
    );
  }, [mapRef, renderFix, startWatch]);

  return { handleLocateMe };
}
